import type { DataTableColumn } from './DataTable';

/** Spec 084 — display formatting and CSV export shared by DataTable. */

const INT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const AED = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PCT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export const NULL_DASH = '—';
export const SUPPRESSED = '<5';

/** One cell as displayed. `pct` values are ratios (0.178 → "17.8%"), like the backend rates and `formatRate`. */
export function formatCell<Row>(col: DataTableColumn<Row>, row: Row): string {
  const v = col.accessor(row);
  if (v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v))) {
    return col.suppressedWhenNull ? SUPPRESSED : NULL_DASH;
  }
  if (typeof v === 'string') return v;
  switch (col.format) {
    case 'int': return INT.format(v);
    case 'aed': return AED.format(v);
    case 'pct': return `${PCT.format(v * 100)}%`;
    default: return String(v);
  }
}

/** RFC 4180: quote when the field has a comma, quote, CR or LF; double inner quotes. */
function quote(field: string): string {
  return /[",\r\n]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field;
}

/**
 * Text that a spreadsheet would run as a formula (leading = + - @, tab or CR) gets a leading apostrophe,
 * so a Meta campaign or ad name can't inject one (Reviewer, #1441). Numbers never come through here.
 */
export function neutraliseFormula(text: string): string {
  return /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
}

/** CSV text: optional first line `range,<from>,<to>,<timezone>`, then headers, then rows as displayed. Null cells are empty, suppressed cells stay "<5". */
export function buildCsv<Row>(
  columns: DataTableColumn<Row>[], rows: Row[], meta?: { from: string; to: string; timezone: string },
): string {
  const lines: string[] = [];
  if (meta) lines.push(['range', meta.from, meta.to, meta.timezone].map(quote).join(','));
  lines.push(columns.map((c) => quote(c.header)).join(','));
  for (const row of rows) {
    lines.push(columns.map((c) => {
      const v = c.accessor(row);
      const isNull = v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v));
      if (isNull && !c.suppressedWhenNull) return '';
      const cell = formatCell(c, row);
      return quote(typeof v === 'string' ? neutraliseFormula(cell) : cell);
    }).join(','));
  }
  return lines.join('\r\n') + '\r\n';
}

/** Saves `csv` as a file through a temporary anchor. Does nothing outside a browser. */
export function downloadCsv(filename: string, csv: string): void {
  if (typeof document === 'undefined' || typeof URL === 'undefined' || typeof Blob === 'undefined') return;
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
